/**
 * CalendarPage (Phase 8h.2) — a DISPATCH PLANNING calendar (V7 §5), not a
 * comprehensive event view. It renders only:
 *   • Leads in stage 'Booked', 'Invoiced' or 'Closed' with a date or pickup window
 *   • Follow-ups where pinned === true
 * Everything else (other stages, unpinned follow-ups) is hidden.
 *
 * Colors for lead events:
 *   Booked + no contractor            → RED
 *   Booked + contractor + !confirmed  → BLUE
 *   Booked + contractor + confirmed   → GREEN
 *   Invoiced                          → TEAL (delivered, money outstanding)
 *   Closed                            → GREY (faded, strikethrough)
 *   Pinned follow-up                  → AMBER
 *
 * Long-distance leads with pickup_start AND pickup_end render as a BAR spanning
 * those days (drop-off is NOT shown — it would collide with future pickups) —
 * until the job is confirmed. A confirmed job (LD or local) has its final pickup
 * day in move_date and shows on that one day. Colour does not change the shape.
 * Bars crossing a week boundary split into one segment per week row. Up to 4
 * lanes show per week; a clickable "+N more" expands the rest.
 *
 * Layout (Group 2e): every week row has a minimum height so the grid reads as a
 * month rather than as a bar chart of how busy each week is; today carries an
 * inset ring that survives chips drawn over it; weekends take a slight wash; and
 * each chip shows move type and arrival time beside the name, since the colour
 * is already spoken for by dispatch state. A Today button sits with the month
 * arrows.
 */

import React, { useMemo, useState } from 'react';
import { tintOf } from '../lib/theme';
import { gate, LoadError } from './States';
import { useApi } from '../hooks/useApi';
import { isLongDistance } from '../lib/moveType';
import { formatDate } from '../lib/utils';
import { useLeads } from '../contexts/LeadsContext';

interface CalendarPageProps {
  onSelectLead: (leadId: string) => void;
}

const RED = 'var(--crm-red)', BLUE = 'var(--crm-accent)', GREEN = 'var(--crm-green)', GREY = 'var(--crm-dim)', AMBER = 'var(--crm-amber)';
// Matches STAGE_COLORS.Invoiced in lib/stages.ts — one teal, two readers.
const TEAL = 'var(--crm-teal)';

/**
 * CELL GEOMETRY.
 *
 * MIN_CELL_H is a floor, not a height: a week with nothing on it used to
 * collapse to about 32px, so the grid's rows were different sizes depending on
 * how busy they were and the month read as a bar chart rather than a calendar.
 * Every row is now at least this tall and still grows for a busy week.
 *
 * BAR_H went from 17 to 22 because a 10px chip in a 15px box could not carry a
 * time as well as a name. Four lanes at 22 plus the day number plus the overflow
 * row is 130px, which is why the floor is 120 and not 160 — an empty month is
 * not improved by being twice as tall as it needs to be.
 */
const HEADER_H = 26, BAR_H = 22, MAX_LANES = 4, MIN_CELL_H = 120;

/** Sunday and Saturday, for the weekend wash. */
const isWeekendCol = (c: number) => c === 0 || c === 6;

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Lead → { color, faded } per the 4-state rule.
function leadColor(l: any): { color: string; faded: boolean } {
  if (l.stage === 'Closed') return { color: GREY, faded: true };
  /**
   * Invoiced is its own colour rather than a shade of Booked or of Closed. The
   * truck has been and the money has not: for dispatch that is finished work,
   * which is why it is not green, and for the office it is live, which is why it
   * is not the faded strikethrough Closed gets.
   */
  if (l.stage === 'Invoiced') return { color: TEAL, faded: false };
  if (l.stage === 'Booked') {
    if (!l.contractor_id) return { color: RED, faded: false };
    if (!l.job_confirmed) return { color: BLUE, faded: false };
    return { color: GREEN, faded: false };
  }
  return { color: GREY, faded: false };
}

// The calendar date range for a lead, or null when it has none.
function leadRange(l: any): { start: string; end: string } | null {
  // Confirmed: the pickup day agreed with the client, not the window.
  if (l.job_confirmed && l.move_date) {
    const d = String(l.move_date).slice(0, 10);
    return { start: d, end: d };
  }
  if (isLongDistance(l) && l.pickup_start && l.pickup_end) {
    return { start: String(l.pickup_start).slice(0, 10), end: String(l.pickup_end).slice(0, 10) };
  }
  if (l.move_date) return { start: String(l.move_date).slice(0, 10), end: String(l.move_date).slice(0, 10) };
  return null;
}

interface Ev {
  key: string; leadId: string; start: string; end: string; label: string;
  color: string; faded: boolean; followup: boolean;
  /** Arrival window start for a job, the scheduled time for a follow-up; '' when unknown. */
  time: string;
  /** LD / LOCAL / F-UP — the kind of thing this is, independent of the colour,
   *  which encodes dispatch STATE and so cannot also encode move type. */
  tag: string;
  /** The whole story, for the hover — a chip one column wide cannot hold it. */
  detail: string;
}

/**
 * '8am - 9:30am' -> '8am'. The window is free text from config_lists, so this
 * takes the first half and does not try to parse a time out of it: a chip has
 * room for the start and the tooltip carries the rest verbatim.
 */
const windowStart = (w: unknown): string => {
  const s = String(w ?? '').trim();
  if (!s) return '';
  return s.split(/\s*[-–]\s*/)[0].trim();
};

/** '09:00:00' -> '9:00'. Postgres TIME, rendered without the seconds. */
const shortTime = (t: unknown): string => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t ?? '').trim());
  if (!m) return '';
  return `${Number(m[1])}:${m[2]}`;
};

export function CalendarPage({ onSelectLead }: CalendarPageProps) {
  const now = new Date();
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [expandedWeeks, setExpandedWeeks] = useState<Record<number, boolean>>({});

  // The jobs for the six weeks on screen (paging 4/4: GET /leads/calendar,
  // read in pages on the server) — not the whole lead list, which stopped at
  // 1,000 rows. Refetched when the month or any lead changes.
  const { version } = useLeads();
  const gridStart = iso(new Date(cursor.getFullYear(), cursor.getMonth(), 1 - new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay()));
  const gridEnd = iso(new Date(cursor.getFullYear(), cursor.getMonth(), 1 - new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay() + 41));
  const { data: leadsData, loading: leadsLoading, error: leadsError, refetch: refetchLeads } = useApi<any[]>(
    async (api) => (await api.getCalendarLeads(gridStart, gridEnd)).data || [],
    { dependencies: [gridStart, gridEnd, version] }
  );
  // Follow-ups carry their lead (job number, name), for the pinned ones.
  const { data: followupsData, error: fuError, refetch: refetchFu } = useApi<any[]>(async (api) => (await api.getFollowUps()).data || []);
  const { data: contractorsData } = useApi<any[]>(async (api) => { try { return (await api.getContractors()).data || []; } catch { return []; } });

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const monthLabel = cursor.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
  const todayStr = iso(new Date());
  const onCurrentMonth = year === now.getFullYear() && month === now.getMonth();

  const contractorName = (id: string | null | undefined) => (contractorsData ?? []).find((k) => k.id === id)?.name;

  // Build the 6×7 grid of actual dates (leading/trailing days included so bars
  // can span across month boundaries).
  const weeks = useMemo(() => {
    const start = new Date(year, month, 1 - firstDay);
    const w: { date: string; dayNum: number; inMonth: boolean; isToday: boolean }[][] = [];
    for (let r = 0; r < 6; r++) {
      const row: any[] = [];
      for (let c = 0; c < 7; c++) {
        const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + r * 7 + c);
        const ds = iso(d);
        row.push({ date: ds, dayNum: d.getDate(), inMonth: d.getMonth() === month, isToday: ds === todayStr });
      }
      w.push(row);
    }
    return w;
  }, [year, month, firstDay, todayStr]);

  // Visible events: Booked/Closed leads with a range + pinned follow-ups.
  const events = useMemo<Ev[]>(() => {
    const out: Ev[] = [];
    for (const l of leadsData ?? []) {
      // Invoiced jobs have HAPPENED — leaving them out would make a delivered
      // move vanish off the month the day it was invoiced.
      if (l.stage !== 'Booked' && l.stage !== 'Invoiced' && l.stage !== 'Closed') continue;
      const range = leadRange(l);
      if (!range) continue;
      const { color, faded } = leadColor(l);
      const cn = contractorName(l.contractor_id);
      const ld = isLongDistance(l);
      const name = `${l.first_name ?? ''} ${(l.last_name ?? '')[0] ?? ''}`.trim();
      out.push({
        key: `l-${l.id}`, leadId: l.id, start: range.start, end: range.end, color, faded, followup: false,
        label: name + (cn ? ` · ${cn}` : ''),
        time: windowStart(l.pickup_arrival_window),
        tag: ld ? 'LD' : 'LOCAL',
        detail: [
          `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim(),
          ld ? 'Long distance' : 'Local move',
          l.move_size || null,
          l.pickup_arrival_window ? `arrival ${l.pickup_arrival_window}` : null,
          cn ? `contractor ${cn}` : 'no contractor',
          l.job_confirmed && l.move_date
            ? `Confirmed pickup: ${formatDate(range.start)}`
            : range.start === range.end ? range.start : `${range.start} → ${range.end}`,
        ].filter(Boolean).join(' · '),
      });
    }
    for (const f of followupsData ?? []) {
      if (!f.pinned || !f.follow_up_date) continue;
      const d = String(f.follow_up_date).slice(0, 10);
      const nm = f.lead ?? null;
      const who = nm ? `${nm.first_name ?? ''} ${(nm.last_name ?? '')[0] ?? ''}`.trim() : 'Follow-up';
      out.push({
        key: `f-${f.id}`, leadId: f.lead_id, start: d, end: d, color: AMBER, faded: false, followup: true,
        label: who,
        time: shortTime(f.follow_up_time),
        tag: 'F-UP',
        detail: ['Pinned follow-up', nm ? `${nm.first_name ?? ''} ${nm.last_name ?? ''}`.trim() : null,
                 f.follow_up_time ? shortTime(f.follow_up_time) : 'no time set', f.description || null, d]
          .filter(Boolean).join(' · '),
      });
    }
    return out;
  }, [leadsData, followupsData, contractorsData]);

  return (
    <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
      {/* An EMPTY calendar and a calendar whose leads failed to load are the same
          picture — and one means "no jobs this month" while the other means "do
          not trust this month". Contractors are a colour lookup only, so a failure
          there does not gate the grid. */}
      {gate('The calendar', leadsLoading, leadsError, !!leadsData, refetchLeads)}
      {!leadsError && fuError && <LoadError what="Follow-ups" error={fuError} onRetry={refetchFu} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Dispatch Calendar</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} style={navBtn} title="Previous month">◀</button>
          <span style={{ fontSize: '14px', fontWeight: 600, minWidth: '150px', textAlign: 'center' }}>{monthLabel}</span>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} style={navBtn} title="Next month">▶</button>
          {/* Three clicks back to where you started is the cost of not having
              this. Dimmed rather than hidden when you are already on this
              month, so the control does not move around under the cursor. */}
          <button
            onClick={() => { setCursor(new Date(now.getFullYear(), now.getMonth(), 1)); setExpandedWeeks({}); }}
            disabled={onCurrentMonth}
            title={onCurrentMonth ? 'Already showing this month' : 'Jump to this month'}
            style={{ ...navBtn, marginLeft: '4px', color: onCurrentMonth ? 'var(--crm-dim)' : 'var(--crm-text)', borderColor: onCurrentMonth ? 'var(--crm-line)' : 'var(--crm-accent-soft-line)', cursor: onCurrentMonth ? 'default' : 'pointer' }}
          >
            Today
          </button>
        </div>
      </div>

      <Legend />

      <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', overflow: 'hidden', marginTop: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--crm-line)' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} style={{ padding: '8px', textAlign: 'center', fontSize: '10px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase' }}>{d}</div>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <WeekRow
            key={wi}
            week={week}
            events={events}
            expanded={!!expandedWeeks[wi]}
            onToggleExpand={() => setExpandedWeeks((p) => ({ ...p, [wi]: !p[wi] }))}
            onSelectLead={onSelectLead}
            isLastRow={wi === weeks.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

function WeekRow({ week, events, expanded, onToggleExpand, onSelectLead, isLastRow }: {
  week: { date: string; dayNum: number; inMonth: boolean; isToday: boolean }[];
  events: Ev[]; expanded: boolean; onToggleExpand: () => void; onSelectLead: (id: string) => void; isLastRow: boolean;
}) {
  const weekStart = week[0].date, weekEnd = week[6].date;
  const colOf = (d: string) => week.findIndex((c) => c.date === d);

  // Clip each event to this week → a segment with startCol..endCol.
  const segments = useMemo(() => {
    const segs: (Ev & { startCol: number; endCol: number; lane: number })[] = [];
    for (const e of events) {
      if (e.end < weekStart || e.start > weekEnd) continue; // no overlap
      const s = e.start < weekStart ? weekStart : e.start;
      const en = e.end > weekEnd ? weekEnd : e.end;
      segs.push({ ...e, startCol: colOf(s), endCol: colOf(en), lane: -1 });
    }
    // Greedy lane assignment (avoid horizontal overlap).
    segs.sort((a, b) => a.startCol - b.startCol || b.endCol - a.endCol);
    const laneEnds: number[] = [];
    for (const s of segs) {
      let lane = 0;
      while (lane < laneEnds.length && laneEnds[lane] >= s.startCol) lane++;
      s.lane = lane;
      laneEnds[lane] = s.endCol;
    }
    return segs;
  }, [events, weekStart, weekEnd]);

  const maxLane = segments.reduce((m, s) => Math.max(m, s.lane), -1);
  const lanesShown = expanded ? maxLane + 1 : Math.min(maxLane + 1, MAX_LANES);
  const visible = segments.filter((s) => expanded || s.lane < MAX_LANES);

  // Per-column hidden count for the "+N more" chips.
  const hiddenPerCol = useMemo(() => {
    const counts = new Array(7).fill(0);
    if (!expanded) {
      for (const s of segments) {
        if (s.lane < MAX_LANES) continue;
        for (let c = s.startCol; c <= s.endCol; c++) counts[c]++;
      }
    }
    return counts;
  }, [segments, expanded]);

  const barsHeight = lanesShown * BAR_H;
  const anyOverflow = hiddenPerCol.some((n: number) => n > 0);
  // A FLOOR, not a height. Every week row is at least MIN_CELL_H so the month
  // reads as a grid of days instead of as a bar chart of how busy each week is,
  // and a busy week still grows past it.
  const cellMinHeight = Math.max(MIN_CELL_H, HEADER_H + barsHeight + (anyOverflow ? 18 : 6));

  return (
    <div style={{ position: 'relative', borderBottom: isLastRow ? 'none' : '1px solid var(--crm-line)' }}>
      {/* Base day grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {week.map((cell, ci) => (
          <div
            key={ci}
            style={{
              minHeight: `${cellMinHeight}px`,
              borderRight: ci < 6 ? '1px solid var(--crm-line)' : 'none',
              padding: '6px',
              /* Today beats the weekend wash, which beats the plain weekday. The
                 wash is deliberately slight — a Saturday job is ordinary here, so
                 the weekend is a reading aid and not a warning. */
              background: cell.isToday ? 'var(--crm-accent-tint)' : isWeekendCol(ci) ? 'var(--crm-panel-2)' : 'transparent',
              /* The old today marker was a faint tint plus a blue day pill, and
                 on a busy row the tint was invisible under the chips. An inset
                 ring survives anything drawn on top of it. */
              boxShadow: cell.isToday ? 'inset 0 0 0 2px var(--crm-accent-line)' : 'none',
              position: 'relative',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '22px', height: '20px', padding: '0 5px', fontSize: '12px', fontWeight: cell.isToday ? 700 : 400, borderRadius: '4px', background: cell.isToday ? 'var(--crm-accent-fill)' : 'transparent', color: cell.isToday ? 'var(--crm-on-fill)' : cell.inMonth ? 'var(--crm-text)' : 'var(--crm-dim)' }}>
                {cell.dayNum}
              </div>
              {cell.isToday && (
                <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--crm-accent)' }}>Today</span>
              )}
            </div>
            {hiddenPerCol[ci] > 0 && (
              <button onClick={onToggleExpand} style={{ position: 'absolute', bottom: '4px', left: '6px', background: 'none', border: 'none', color: 'var(--crm-muted)', cursor: 'pointer', fontSize: '10px', padding: 0 }}>
                +{hiddenPerCol[ci]} more
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Bars overlay */}
      <div style={{ position: 'absolute', top: `${HEADER_H}px`, left: 0, right: 0, height: `${barsHeight}px`, pointerEvents: 'none' }}>
        {visible.map((s) => {
          const cols = s.endCol - s.startCol + 1;
          return (
            <div
              key={s.key}
              onClick={() => onSelectLead(s.leadId)}
              title={s.detail}
              style={{
                position: 'absolute',
                left: `calc(${(s.startCol / 7) * 100}% + 3px)`,
                width: `calc(${(cols / 7) * 100}% - 6px)`,
                top: `${s.lane * BAR_H}px`,
                height: `${BAR_H - 3}px`,
                background: tintOf(s.color, s.faded ? 13 : 20),
                border: `1px solid ${tintOf(s.color, s.faded ? 33 : 60)}`,
                borderLeft: `3px solid ${s.color}`,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '0 5px',
                overflow: 'hidden',
                cursor: 'pointer',
                pointerEvents: 'auto',
                opacity: s.faded ? 0.75 : 1,
              }}
            >
              {/* Type, time, who — in that order, because the first two are
                  short and fixed-width and the name is what gets ellipsed when
                  the column runs out. The colour already carries dispatch
                  STATE, so the move type has to be written down. */}
              <span style={{ fontSize: '8px', fontWeight: 800, letterSpacing: '0.4px', color: s.color, opacity: 0.85, flexShrink: 0 }}>{s.tag}</span>
              {s.time && (
                <span style={{ fontSize: '10px', fontFamily: "'DM Mono', monospace", color: s.color, opacity: 0.9, flexShrink: 0 }}>{s.time}</span>
              )}
              <span
                style={{
                  fontSize: '11px', color: s.color, fontWeight: s.followup ? 700 : 500,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  textDecoration: s.faded ? 'line-through' : 'none',
                }}
              >
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Legend() {
  const items = [
    { c: RED, t: 'Booked · no contractor' },
    { c: BLUE, t: 'Contractor assigned' },
    { c: GREEN, t: 'Job confirmed' },
    { c: TEAL, t: 'Invoiced · unpaid' },
    { c: GREY, t: 'Closed' },
    { c: AMBER, t: 'Pinned follow-up' },
  ];
  return (
    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '11px', color: 'var(--crm-muted)' }}>
      {items.map((i) => (
        <span key={i.t} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: `${tintOf(i.c, 33)}`, border: `1px solid ${i.c}` }} />
          {i.t}
        </span>
      ))}
    </div>
  );
}

const navBtn: React.CSSProperties = { padding: '6px 12px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' };
